import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { createEditor, Descendant, Element as SlateElement } from 'slate';
import { Slate, Editable, withReact, RenderElementProps, RenderLeafProps } from 'slate-react';
import { withHistory } from 'slate-history';
import { 
  MJEditorProps, 
  CustomEditor, 
  CustomElement, 
  CustomText,
  ParagraphElement 
} from '../types';
import Toolbar from './Toolbar';

// Create a custom editor with React and History plugins
const createMJEditor = () => withHistory(withReact(createEditor()));

// Default value for the editor
const createDefaultValue = (): CustomElement[] => [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

// Render element function
const renderElement = (props: RenderElementProps): JSX.Element => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case 'paragraph':
      return (
        <p
          {...attributes}
          style={{
            textAlign: (element as any).align || 'left',
            marginLeft: (element as any).indent ? `${(element as any).indent * 20}px` : undefined,
          }}
        >
          {children}
        </p>
      );
    case 'heading':
      const level = (element as any).level || 1;
      const HeadingTag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return (
        <HeadingTag
          {...attributes}
          style={{
            textAlign: (element as any).align || 'left',
          }}
        >
          {children}
        </HeadingTag>
      );
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>;
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'image':
      return (
        <div
          {...attributes}
          style={{
            textAlign: (element as any).align || 'left',
          }}
        >
          <div contentEditable={false}>
            <img
              src={(element as any).url}
              alt={(element as any).alt || ''}
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
            {(element as any).caption && (
              <div style={{ textAlign: 'center', fontSize: '0.9em', color: '#666' }}>
                {(element as any).caption}
              </div>
            )}
          </div>
          {children}
        </div>
      );
    case 'code-block':
      return (
        <pre {...attributes}>
          <code>{children}</code>
        </pre>
      );
    case 'horizontal-rule':
      return (
        <hr
          {...attributes}
          style={{
            border: 'none',
            borderTop: `${(element as any).thickness || 1}px ${(element as any).style || 'solid'} ${(element as any).color || '#ccc'}`,
            margin: '1em 0',
          }}
        />
      );
    case 'checklist':
      return (
        <div {...attributes}>
          <input
            type="checkbox"
            checked={(element as any).checked}
            readOnly
            style={{ marginRight: '0.5em' }}
          />
          {children}
        </div>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

// Render leaf function
const renderLeaf = (props: RenderLeafProps): JSX.Element => {
  const { attributes, children, leaf } = props;

  let element = <span {...attributes}>{children}</span>;

  if ((leaf as any).bold) {
    element = <strong>{element}</strong>;
  }

  if ((leaf as any).italic) {
    element = <em>{element}</em>;
  }

  if ((leaf as any).underline) {
    element = <u>{element}</u>;
  }

  if ((leaf as any).strikethrough) {
    element = <del>{element}</del>;
  }

  if ((leaf as any).superscript) {
    element = <sup>{element}</sup>;
  }

  if ((leaf as any).subscript) {
    element = <sub>{element}</sub>;
  }

  if ((leaf as any).color) {
    element = (
      <span style={{ color: (leaf as any).color }}>{element}</span>
    );
  }

  if ((leaf as any).backgroundColor) {
    element = (
      <span style={{ backgroundColor: (leaf as any).backgroundColor }}>{element}</span>
    );
  }

  if ((leaf as any).fontSize) {
    element = (
      <span style={{ fontSize: `${(leaf as any).fontSize}pt` }}>{element}</span>
    );
  }

  if ((leaf as any).fontFamily) {
    element = (
      <span style={{ fontFamily: (leaf as any).fontFamily }}>{element}</span>
    );
  }

  if ((leaf as any).link) {
    element = (
      <a href={(leaf as any).link} target="_blank" rel="noopener noreferrer">
        {element}
      </a>
    );
  }

  return element;
};

export const MJEditor: React.FC<MJEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  readOnly = false,
  plugins = [],
  theme = 'light',
  className = '',
  style = {},
  autoFocus = false,
  autoSave = false,
  autoSaveInterval = 30000,
  onSave,
  maxLength,
  minHeight = '200px',
  maxHeight,
  toolbarConfig,
}) => {
  const editor = useMemo(() => createMJEditor(), []);
  const autoSaveRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValue = useRef<CustomElement[]>([]);

  // Handle auto-save
  useEffect(() => {
    if (autoSave && onSave && value) {
      // Clear existing timer
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
      }

      // Set new timer
      autoSaveRef.current = setTimeout(() => {
        if (JSON.stringify(value) !== JSON.stringify(lastSavedValue.current)) {
          onSave(value);
          lastSavedValue.current = JSON.parse(JSON.stringify(value));
        }
      }, autoSaveInterval);
    }

    return () => {
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
      }
    };
  }, [autoSave, autoSaveInterval, onSave, value]);

  // Handle max length
  const handleChange = useCallback((newValue: Descendant[]) => {
    if (maxLength) {
      const textLength = newValue.reduce((length, node) => {
        if (SlateElement.isElement(node)) {
          return length + node.children.reduce((childLength, child) => {
            if (typeof child === 'object' && 'text' in child) {
              return childLength + (typeof child.text === 'string' ? child.text.length : 0);
            }
            return childLength;
          }, 0);
        }
        if (typeof node === 'object' && 'text' in node) {
          return length + (typeof node.text === 'string' ? node.text.length : 0);
        }
        return length;
      }, 0);

      if (textLength > maxLength) {
        return; // Don't update if exceeding max length
      }
    }

    onChange?.(newValue as CustomElement[]);
  }, [onChange, maxLength]);

  // Handle key down events
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Handle plugin key events
    for (const plugin of plugins) {
      if (plugin.onKeyDown && plugin.onKeyDown(event as any, editor)) {
        return;
      }
    }

    // Default keyboard shortcuts
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'b':
          event.preventDefault();
          // Toggle bold
          break;
        case 'i':
          event.preventDefault();
          // Toggle italic
          break;
        case 'u':
          event.preventDefault();
          // Toggle underline
          break;
        case 'z':
          if (event.shiftKey) {
            event.preventDefault();
            // Redo
          } else {
            event.preventDefault();
            // Undo
          }
          break;
      }
    }
  }, [editor, plugins]);

  // Combine plugin render functions
  const combinedRenderElement = useCallback((props: RenderElementProps): JSX.Element => {
    // Try plugin render functions first
    for (const plugin of plugins) {
      if (plugin.renderElement) {
        const result = plugin.renderElement(props);
        if (result) return result as JSX.Element;
      }
    }
    
    // Fall back to default render function
    return renderElement(props);
  }, [plugins]);

  const combinedRenderLeaf = useCallback((props: RenderLeafProps): JSX.Element => {
    // Try plugin render functions first
    for (const plugin of plugins) {
      if (plugin.renderLeaf) {
        const result = plugin.renderLeaf(props);
        if (result) return result as JSX.Element;
      }
    }
    
    // Fall back to default render function
    return renderLeaf(props);
  }, [plugins]);

  const editorValue = value || createDefaultValue();

  return (
    <div
      className={`mj-editor ${theme} ${className}`}
      style={{
        minHeight,
        maxHeight,
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      <Slate
        editor={editor}
        value={editorValue}
        onChange={handleChange}
      >
        <Toolbar config={toolbarConfig || {
          showTextFormatting: true,
          showFontControls: true,
          showTextStyling: true,
          showTextAlignment: true,
          showIndentation: true,
          showLists: true,
          showTextPositioning: true,
          showInsertOptions: true,
          showAdvancedFeatures: true,
        }} />
        <div style={{ 
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          minHeight: '300px',
          maxHeight: maxHeight ? `calc(${maxHeight} - 56px)` : 'none'
        }}>
          <Editable
            placeholder={placeholder}
            readOnly={readOnly}
            autoFocus={autoFocus}
            renderElement={combinedRenderElement}
            renderLeaf={combinedRenderLeaf}
            onKeyDown={handleKeyDown}
            style={{
              outline: 'none',
              minHeight: '100%',
              padding: '24px',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </Slate>
    </div>
  );
};

export default MJEditor; 